import { ReactElement, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { getPdfPageCount } from '@mutations'

interface StoreyImagePdfPageDialogProps {
  open: boolean
  file: File | null
  storey: string
  onConfirm: (page: number) => void
  onClose: () => void
}

const StoreyImagePdfPageDialog = ({
  open,
  file,
  storey,
  onConfirm,
  onClose,
}: StoreyImagePdfPageDialogProps): ReactElement => {
  const { projectId } = useParams()
  // Raw input value so the field can be cleared while typing (e.g. deleting the default "1"
  // before entering another page); parsed only for validation and confirm.
  const [pageInput, setPageInput] = useState('1')
  const page = parseInt(pageInput, 10)
  const [pageCount, setPageCount] = useState<number | null>(null)
  const [isLoadingPageCount, setIsLoadingPageCount] = useState(false)
  const [pageCountError, setPageCountError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !file || !projectId) {
      setIsLoadingPageCount(false)
      return
    }

    setPageInput('1')
    setPageCount(null)
    setPageCountError(null)
    setIsLoadingPageCount(true)

    let cancelled = false

    getPdfPageCount
      .request(projectId, file)
      .then(result => {
        if (cancelled) return
        setPageCount(result.page_count)
      })
      .catch((error: AxiosError) => {
        if (cancelled) return
        setPageCountError(buildErrorMessage(error, 'PDF konnte nicht gelesen werden'))
      })
      .finally(() => {
        if (cancelled) return
        setIsLoadingPageCount(false)
      })

    return () => {
      cancelled = true
    }
  }, [file, open, projectId])

  const isPageValid =
    Number.isInteger(page) && page >= 1 && (pageCount === null || page <= pageCount)

  const handleConfirm = () => {
    if (!isPageValid) return
    onConfirm(page)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-cy={`dialog-storey-${storey}-pdf-page`}
    >
      <DialogTitle>PDF-Seite für {storey}. SW auswählen</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Wählen Sie die PDF-Seite, die als Grundrissbild für dieses Stockwerk verwendet werden
            soll.
          </Typography>

          {isLoadingPageCount ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">PDF wird gelesen…</Typography>
            </Stack>
          ) : null}

          {pageCountError ? (
            <Typography variant="body2" color="error">
              {pageCountError}
            </Typography>
          ) : null}

          {pageCount !== null ? (
            <Typography variant="body2">Dieses PDF hat {pageCount} Seite(n).</Typography>
          ) : null}

          <TextField
            label="Seite"
            type="number"
            size="small"
            value={pageInput}
            inputProps={{
              min: 1,
              max: pageCount ?? undefined,
              step: 1,
              'data-cy': `input-storey-${storey}-pdf-page`,
            }}
            onChange={event => setPageInput(event.target.value)}
            disabled={isLoadingPageCount}
            helperText="Seitennummer beginnt bei 1"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-cy={`btn-storey-${storey}-pdf-page-cancel`}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isPageValid || isLoadingPageCount || !!pageCountError}
          data-cy={`btn-storey-${storey}-pdf-page-confirm`}
        >
          Hochladen
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StoreyImagePdfPageDialog
